import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, firstValueFrom, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiService } from '@core/services/api.service';
import { AuthApiService } from '@core/services/auth-api.service';
import {
  LoginRequest,
  LoginResponse,
  User,
  OtpRequest,
  OtpVerificationResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ValidateOtpResponse,
  ResetPasswordResponse,
  isPortalLoginAllowed,
  PortalAccessDeniedError
} from '../models/auth.models';
import { buildAccessProfile, landingRouteFor } from '@core/models/permissions.model';
import { clearAllCached } from '@shared/utils/ttl-cache';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSignal = signal<User | null>(null);
  private tokenSignal = signal<string | null>(null);
  private refreshTokenSignal = signal<string | null>(null);
  private pendingVerificationEmailSignal = signal<string | null>(null);

  // Computed signals for reactive state
  currentUser = this.currentUserSignal.asReadonly();
  isAuthenticated = computed(() => !!this.tokenSignal() && !!this.currentUserSignal());
  isProfileSetup = computed(() => !!this.currentUserSignal()?.isProfileSetup);
  pendingVerificationEmail = this.pendingVerificationEmailSignal.asReadonly();

  constructor(
    private apiService: ApiService,
    private authApiService: AuthApiService,
    private router: Router
  ) {
    this.initializeUserFromStorage();
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      // FE-204: an email is case-insensitive, so the portal never sends the
      // casing the user happened to type. Normalising here (rather than in the
      // login form) covers every caller and keeps the persisted pending email
      // consistent with what the OTP endpoints are later asked to match.
      const email = AuthService.normalizeEmail(credentials.email);
      const loginResponse = await this.performLogin({ ...credentials, email });

      // Handle the backend response format
      if (loginResponse.otpRequired) {
        // OTP is required - store email for OTP verification
        this.pendingVerificationEmailSignal.set(email);
        localStorage.setItem('pendingVerificationEmail', email);

        // Start OTP request process
        await this.requestOtp(email);
      } else if (loginResponse.accessToken && loginResponse.refreshToken) {
        // No-OTP login (e.g. a portal user created with requireOtpAtLogin=false,
        // or a teacher). The BE returns verified tokens but NO user object, so
        // store the tokens and hydrate the profile the same way the post-OTP
        // path does — this is also where the token's portalAccess scope claims
        // get merged onto the user.
        localStorage.setItem('token', loginResponse.accessToken);
        localStorage.setItem('refreshToken', loginResponse.refreshToken);
        this.tokenSignal.set(loginResponse.accessToken);
        this.refreshTokenSignal.set(loginResponse.refreshToken);
        try {
          await this.fetchAndStoreUserProfile();
        } catch (profileError) {
          if (profileError instanceof PortalAccessDeniedError) {
            this.clearStorageData();
            this.clearSignalData();
            throw profileError;
          }
          // Still authenticated via the stored tokens even if the profile
          // hydrate failed; buildAccessProfile will just see no scope claims.
        }
      }

      return loginResponse;
    } catch (error) {
      throw error;
    }
  }

  async verifyOtp(otpCode: string): Promise<void> {
    const email = this.getUserEmailForOtp();
    if (!email) throw new Error('No email found for OTP verification');

    const verificationResponse = await this.performOtpVerification(email, otpCode);

    if (!verificationResponse.success) {
      throw new Error('OTP verification failed');
    }

    // Store tokens first
    this.storeTokensFromOtpResponse(verificationResponse);
    this.updateTokenSignals(verificationResponse);

    try {
      // Then fetch the profile — this is also the portal eligibility check.
      await this.fetchAndStoreUserProfile();
    } catch (profileError) {
      if (profileError instanceof PortalAccessDeniedError) {
        // Drop the just-issued tokens so no session survives the rejection.
        this.clearStorageData();
        this.clearSignalData();
        throw profileError;
      }
      // Don't fail the entire verification if profile fetch fails
      // The user is still authenticated with tokens
    }

    this.clearPendingVerificationEmail();
  }

  refreshAccessToken(): Observable<string> {
    const currentRefreshToken = this.refreshTokenSignal();
    if (!currentRefreshToken) {
      console.error('❌ No refresh token available');
      return throwError(() => new Error('No refresh token available'));
    }
    return this.authApiService.post<RefreshTokenResponse>('/auth/refresh-token', { 
      refreshToken: currentRefreshToken 
    }).pipe(
      map((response: RefreshTokenResponse) => {
        if (!response) throw new Error('Token refresh failed');
        this.updateTokens(response);
        return response.accessToken;
      }),
      catchError(error => {
        console.error('❌ Token refresh failed with error:', error);
        console.error('❌ Error status:', error?.status);
        console.error('❌ Error message:', error?.message);
        console.error('❌ Error body:', error?.error);
        
        // If it's a 401, the refresh token is invalid
        if (error?.status === 401) {
          console.error('❌ Refresh token is invalid or expired, clearing auth data');
          this.clearStorageData();
          this.clearSignalData();
        }
        
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    // Clear all authentication data
    this.clearStorageData();
    this.clearSignalData();
    
    // Ensure we navigate to login
    this.navigateToLogin();
  }

  getToken(): string | null {
    return this.tokenSignal();
  }

  getRefreshToken(): string | null {
    return this.refreshTokenSignal();
  }

  getUserEmailForOtp(): string | null {
    return this.pendingVerificationEmailSignal() || this.currentUserSignal()?.email || null;
  }

  /**
   * Teacher Portal User (Dismissal Call). Now derived from the BE-288 permission
   * matrix: a "call-screen-only" user (only granted module is dismissal-call) is
   * the generalization of the old DISMISSAL_TEACHER role, which still counts as a
   * fallback until BE ships module_permissions. Drives the teacher-only redirect,
   * read-only filter scope, and board scoping.
   */
  static readonly DISMISSAL_TEACHER_ROLE = 'DISMISSAL_TEACHER';

  isDismissalTeacher(): boolean {
    return buildAccessProfile(this.currentUserSignal()).callScreenOnly;
  }

  /**
   * Post-login landing route. Call-screen-only users go straight to the
   * fullscreen dismissal filter (no admin shell); everyone else lands on the
   * dashboard.
   */
  getLandingRoute(): string {
    return landingRouteFor(buildAccessProfile(this.currentUserSignal()));
  }

  /**
   * The teacher's single assigned Business + Branch (id + display name), read
   * from the logged-in profile. BE must surface these on the user object (see
   * Teacher Portal handoff §2); we read defensively across the likely shapes so
   * the exact field name can be finalised without another FE change.
   */
  getAssignedScope(): { businessId: string; businessName: string; branchId: string; branchName: string } {
    const user = this.currentUserSignal() as any;
    const business = user?.assignedBusiness ?? user?.business ?? (Array.isArray(user?.businesses) ? user.businesses[0]?.business ?? user.businesses[0] : null);
    const branch = user?.assignedBranch ?? user?.branch ?? (Array.isArray(user?.branches) ? user.branches[0]?.branch ?? user.branches[0] : null);
    // The BE-288 scope claims (branchIds/businessIds) are the only reliably-populated
    // source of a portal user's assignment — they're merged from the access token even
    // when the profile RTO omits assigned business/branch objects. Use the first real
    // (non-wildcard) id as the last-resort fallback so the teacher's branch still resolves.
    const scopedBusinessId = Array.isArray(user?.businessIds) ? user.businessIds.find((id: string) => id && id !== '*') : undefined;
    const scopedBranchId = Array.isArray(user?.branchIds) ? user.branchIds.find((id: string) => id && id !== '*') : undefined;
    return {
      businessId: business?.id ?? business?._id ?? user?.businessId ?? scopedBusinessId ?? '',
      businessName: business?.fullNameEn ?? business?.name ?? business?.nameEn ?? '',
      branchId: branch?.id ?? branch?._id ?? user?.branchId ?? scopedBranchId ?? '',
      branchName: branch?.fullNameEn ?? branch?.name ?? branch?.nameEn ?? ''
    };
  }

  async requestOtp(email: string): Promise<void> {
    await firstValueFrom(
      this.authApiService.post('/auth/otp/request', {
        email: AuthService.normalizeEmail(email),
        deliveryMethod: 'email',
        type: 'LOGIN'
      })
    );
  }

  // ─── FE-204 password reset ────────────────────────────────────────────────
  // The BE has no reset-token flow: a PASSWORD_RESET OTP is requested, then
  // spent directly by /auth/otp/reset-password. `validate` exists so the code
  // can be checked before the user is asked for a new password — it explicitly
  // does NOT consume the code, so the reset call can still use it.

  /** Send a PASSWORD_RESET OTP to the account's email. */
  async requestPasswordResetOtp(email: string): Promise<void> {
    await firstValueFrom(
      this.authApiService.post('/auth/otp/request', {
        email: AuthService.normalizeEmail(email),
        deliveryMethod: 'email',
        type: 'PASSWORD_RESET'
      })
    );
  }

  /**
   * Check a reset code without spending it. The BE 400s on an invalid or
   * expired code; a 200 carrying `valid: false` is treated the same way so the
   * caller only ever has to handle a rejection.
   */
  async validatePasswordResetOtp(email: string, otpCode: string): Promise<void> {
    const response = await firstValueFrom(
      this.authApiService.post<ValidateOtpResponse>('/auth/otp/validate', {
        email: AuthService.normalizeEmail(email),
        otpCode,
        type: 'PASSWORD_RESET'
      })
    );

    if (response && response.valid === false) {
      throw new Error(response.message || 'Invalid or expired code');
    }
  }

  /** Spend the reset code and set the new password. */
  async resetPassword(email: string, otpCode: string, newPassword: string): Promise<void> {
    await firstValueFrom(
      this.authApiService.post<ResetPasswordResponse>('/auth/otp/reset-password', {
        email: AuthService.normalizeEmail(email),
        otpCode,
        newPassword
      })
    );
  }

  /**
   * Emails are case-insensitive. Applied to every email leaving the portal so a
   * user who types `Rana.H@Waki.SA` is the same user who registered as
   * `rana.h@waki.sa` (FE-204).
   */
  private static normalizeEmail(email: string): string {
    return (email ?? '').trim().toLowerCase();
  }

  // ✅ Private helper methods (small and focused)
  private async performLogin(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await firstValueFrom(
        this.authApiService.post<LoginResponse>('/auth/login', credentials)
      );

      if (!response) {
        throw new Error('Login failed - no response received');
      }

      return response;
    } catch (error) {
      throw error;
    }
  }

  private async performOtpVerification(email: string, otpCode: string): Promise<OtpVerificationResponse> {
    const response = await firstValueFrom(
      this.authApiService.post<OtpVerificationResponse>('/auth/otp/verify', {
        email: AuthService.normalizeEmail(email),
        otpCode
      })
    );
    if (!response) throw new Error('OTP verification failed');
    return response;
  }

  private async performTokenRefresh(refreshToken: string): Promise<RefreshTokenResponse> {
    const response = await firstValueFrom(
      this.authApiService.post<RefreshTokenResponse>('/auth/refresh-token', { refreshToken })
    );
    if (!response) throw new Error('Token refresh failed');
    return response;
  }

  private getCurrentUserId(): string | null {
    return this.currentUserSignal()?._id ?? null;
  }

  private handleSuccessfulLogin(response: LoginResponse, email: string): void {
    if (!response.accessToken || !response.refreshToken || !response.user) {
      throw new Error('Invalid login response - missing required data');
    }

    this.storeAuthData(response.accessToken, response.refreshToken, response.user);
    this.updateSignals(response.accessToken, response.refreshToken, response.user);
  }

  private storeAuthData(accessToken: string, refreshToken: string, user: User): void {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
  }

  private updateSignals(accessToken: string, refreshToken: string, user: User): void {
    this.tokenSignal.set(accessToken);
    this.refreshTokenSignal.set(refreshToken);
    this.currentUserSignal.set(user);
  }

  private updateTokens(response: RefreshTokenResponse): void {
    this.tokenSignal.set(response.accessToken);
    this.refreshTokenSignal.set(response.refreshToken);
    localStorage.setItem('token', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
  }

  private clearPendingVerificationEmail(): void {
    this.pendingVerificationEmailSignal.set(null);
    localStorage.removeItem('pendingVerificationEmail');
  }

  private clearStorageData(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('pendingVerificationEmail');
    // TTL-cached reads (sidebar pending badges) are per-user; drop them so the
    // next account never paints the previous one's numbers.
    clearAllCached();
  }

  private clearSignalData(): void {
    this.currentUserSignal.set(null);
    this.tokenSignal.set(null);
    this.refreshTokenSignal.set(null);
    this.pendingVerificationEmailSignal.set(null);
  }

  /**
   * Extract the BE-288 scope claims embedded in the access token. The
   * /users/profile endpoint returns none of these, so the token is the only
   * source of a portal user's granted modules and branch scope.
   */
  private decodeTokenClaims(token: string | null): Partial<User> {
    if (!token) return {};
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return {};
      const payload = JSON.parse(atob(parts[1]));
      return {
        portalAccess: payload.portalAccess ?? null,
        branchIds: payload.branchIds ?? [],
        businessIds: payload.businessIds ?? [],
        serviceNames: payload.serviceNames ?? [],
        roles: payload.roles ?? [],
      };
    } catch (error) {
      console.error('Error decoding token claims:', error);
      return {};
    }
  }

  // ✅ Check if JWT token is expired
  private isTokenExpired(token: string): boolean {
    if (!token) return true;
    
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return true;
      
      const payload = JSON.parse(atob(tokenParts[1]));
      const exp = payload.exp;
      
      if (!exp) return true;
      
      // Add 30 second buffer to account for clock skew
      const currentTime = Math.floor(Date.now() / 1000);
      return currentTime >= (exp - 30);
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  private navigateToLogin(): void {
    // Force navigation to login page
    this.router.navigate(['/auth/login']).then(success => {
      if (success) {
      } else {
        console.error('[Auth Service] Failed to navigate to login page');
        // Force reload to login page as fallback
        window.location.href = '/auth/login';
      }
    });
  }

  private initializeUserFromStorage(): void {
    const storedToken = localStorage.getItem('token');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const storedUserJson = localStorage.getItem('user');
    const storedPendingEmail = localStorage.getItem('pendingVerificationEmail');

    if (!storedToken || !storedUserJson) {
      // Even if no user data, we might have a pending email for OTP
      if (storedPendingEmail) {
        this.pendingVerificationEmailSignal.set(storedPendingEmail);
      }
      return;
    }

    try {
      const storedUser = JSON.parse(storedUserJson);
      // Sessions persisted before the portal gate existed (or tampered with) are
      // re-checked on boot. Only judge users that actually carry a userType —
      // the minimal-user fallback has none and must stay usable.
      if (storedUser?.userType && !isPortalLoginAllowed(storedUser)) {
        this.clearStorageData();
        return;
      }
      this.tokenSignal.set(storedToken);
      this.refreshTokenSignal.set(storedRefreshToken);
      // Re-apply the token's BE-288 scope claims (portalAccess, roles, branch/
      // business ids) over the stored profile. This heals sessions persisted
      // before the claims were merged — or from a failed /users/profile fetch —
      // so they scope correctly instead of defaulting to unrestricted.
      const merged = { ...storedUser, ...this.decodeTokenClaims(storedToken) } as User;
      this.currentUserSignal.set(merged);

      if (storedPendingEmail) {
        this.pendingVerificationEmailSignal.set(storedPendingEmail);
      }
    } catch (parseError) {
      console.error('Failed to parse stored user data:', parseError);
      this.clearStorageData();
    }
  }

  private storeTokensFromOtpResponse(response: OtpVerificationResponse): void {
    localStorage.setItem('token', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
  }

  private updateTokenSignals(response: OtpVerificationResponse): void {
    this.tokenSignal.set(response.accessToken);
    this.refreshTokenSignal.set(response.refreshToken);
  }

  private async fetchAndStoreUserProfile(): Promise<void> {
    try {
      const profileResponse = await firstValueFrom(
        this.authApiService.get<{profile: User}>('/users/profile')
      );

      if (profileResponse?.profile) {
        // Portal eligibility gate: the tokens are already issued at this point,
        // so the profile is the first (and only) place we learn the account type.
        // A non-eligible account must never end up with a stored session — the
        // caller clears the tokens when it sees this error.
        if (!isPortalLoginAllowed(profileResponse.profile)) {
          throw new PortalAccessDeniedError();
        }

        // The /users/profile RTO carries no scope data — the BE-288 scope claims
        // (portalAccess, branchIds, businessIds, serviceNames, roles) live only
        // in the access token. Decode and merge them so buildAccessProfile can
        // scope the portal instead of defaulting the user to unrestricted.
        const merged = { ...profileResponse.profile, ...this.decodeTokenClaims(this.tokenSignal()) } as User;
        this.currentUserSignal.set(merged);
        localStorage.setItem('user', JSON.stringify(merged));
      } else {
        throw new Error('Profile data is missing from response');
      }
    } catch (error) {
      // A rejected account is not a failed fetch — never fall back to a minimal
      // user for it, that would hand it a working session.
      if (error instanceof PortalAccessDeniedError) throw error;

      // Profile fetch failed (e.g. /users/profile 403s for a scoped portal
      // user). Keep the session alive with a minimal user — but STILL merge the
      // token's BE-288 scope claims (portalAccess, roles, branchIds, businessIds)
      // so buildAccessProfile restricts the portal instead of falling back to
      // "unrestricted" and showing the whole sidebar. Token claims win over the
      // placeholder fields below (so `roles` becomes the real BUSINESS_USER role).
      const minimalUser: User = {
        _id: 'temp-id',
        email: this.getUserEmailForOtp() || '',
        firstName: {
          en: '',
          ar: ''
        },
        lastName: {
          en: '',
          ar: ''
        },
        roles: ['user'],
        permissions: [],
        isProfileSetup: true,
        ...this.decodeTokenClaims(this.tokenSignal()),
      };

      this.currentUserSignal.set(minimalUser);
      localStorage.setItem('user', JSON.stringify(minimalUser));

      throw error; // Still throw to let caller know there was an issue
    }
  }
}